import { Translation } from "@talladega/types";
import { Instant, LocalDate } from "@js-joda/core";
import { PurchaseNotFoundResponse } from "@modules/purchase/warranty/types/Purchase";
import { LenderSubmission } from "./LenderSubmission";

type Vehicle = {
  vin: string;
  odometerCount: number;
  purchaseDate: string | null;
  purchasePrice?: number | null;
  vehiclePrice?: number | null;
  availableTrims: string[];
  trim?: string;
  year?: number;
  make?: string;
  model?: string;
  stockNumber?: string;
  image?: string;
  exteriorColour: string | null;
  chromeDecodedData: {
    year: number;
    make: string;
    model: string;
    trims: string[];
  } | null;
  availableExteriorColours: string[];
  bodyStyle?: string;
} | null;

interface Address {
  addressTypeId: number | null;
  addressNumber: string;
  street: string;
  suiteNumber: string;
  fullAddress: string;
  postalCode: string;
  streetTypeId: number | null;
  city: string;
  provinceId: number | null;
  provinceAbbr: string;
  startLivingDate: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface BankInformation {
  financialInstitutionName: string | null;
  maskedBankAccountNumber: string | null;
  lastPayoutConfigurationAttemptedAt: string | null;
}

interface Mortgage {
  homeTypeId: number | null;
  marketValue: number | null;
  mortgageAmount: number | null;
  monthlyPayment: number | null;
  mortgageHolder: string;
  mortgageAdditionalInfo: string;
}

interface Employment {
  employer: string;
  employerPhoneNumber: string;
  occupation: string;
  typeId: number;
  startDate: string;
  statusId: number;
  city: string;
  provinceId: number;
  addressTypeId: number | null;
  addressNumber: string;
  street: string;
  suiteNumber: string;
  postalCode: string;
  streetTypeId: number | null;
}

interface Income {
  grossIncome: number | null;
  grossIncomeBasisId: number | null;
  otherIncomeTypeId: number | null;
  otherIncome: number | null;
  otherIncomeBasisId: number | null;
  otherIncomeDescription: string | null;
}

export type ConsumerFinancingCustomer = {
  userId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  dateOfBirth: string | null;
  salutationId: number;
  suffixId: number | null;
  sexId: number | null;
  maritalStatusId: number | null;
  agreedVehicleDetailsTermsAndConditionsAt: Instant | null;
  agreedCustomerDetailsTermsAndConditionsAt: Instant | null;
  typeId: number | null;
  payoutMethodVerified: boolean;
  payoutMethodCodeSet: boolean;
  currentAddress: Address | null;
  previousAddress: Address | null;
  mortgage: Mortgage | null;
  income: Income | null;
  currentEmployment: Employment | null;
  previousEmployment: Employment | null;
  bankInformation: BankInformation | null;
  payoutSecurityAnswer: string | null;
};

interface DealDocument {
  id: string;
  name: string;
  documentTypeId: number;
  scanResultId: number;
}

type ProductDocument = {
  id: string;
  contractNumber: string;
};

type ConsumerFinancingDocuments = {
  anyDocumentIsSigned: boolean;
  anyDocumentRequiresCreate: boolean;
  documentsAreSigned: boolean;
  eSigningStarted: boolean;
  dealDocuments: DealDocument[] | null;
  activeDealProductDocuments: ProductDocument[];
};

export type ConsumerFinancingTodo = {
  dealTodoId: string;
  todoId: number;
  name: Translation;
  todoTypeId: number;
  completedAt: string | null;
};

type DealSourceInfo = {
  adId?: string | null;
  userId?: string | null;
  sessionId?: string | null;
  appId?: string | null;
  locationId?: string | null;
  ctaId?: string | null;
};

type Tax = {
  name: string;
  amount: number;
  rate: number;
};

export type ConsumerFinancingPricing = {
  products: {
    name: string;
    price: number;
  }[];
  taxes: Tax[];
  subTotal: number;
  discount: number;
  totalPrice: number;
  amountFinanced: number | null;
  cashDownAmount: number | null;
  paymentFrequencyId: number | null;
  term: number | null;
  payment: number;
};

type ConsumerFinancingProductConfiguration = Record<
  string,
  string | number | LocalDate | undefined
>;

type ConsumerFinancingDropOffDetails = {
  dropOffLocationId: string | null;
  dropOffLocationAppointmentAt: string | null;
  dropOffLocationName: string | null;
};

export type IncludedAddOn = {
  code: string;
  name: string;
};

export type ProductAddOn = {
  code: string;
};

export type RateConfiguration = {
  programId: string;
  coverageTypeId: string;
  classCode: string;
  term: number;
  termKms: number;
  deductible: number;
  provinceAbbr: string;
  condition: string;
  selectedProductAddOns: ProductAddOn[];
  unitPurchaseDate: LocalDate;
  termExpiryDate: LocalDate | null;
  paymentFrequencyId: number | null;
};

export type ProductRateRequest = {
  dealId: string;
  productId: string;
  productConfiguration: RateConfiguration;
  locale?: string;
};

export type StartConsumerFinancingRequest = {
  vehicleStockNumber?: string | null;
  vin?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  coverImage?: string | null;
  mileage?: number | null;
  purchaseDate?: LocalDate | null;
  purchasePrice?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  sourceAdId?: string | null;
  sourceUserId?: string | null;
  sourceSessionId?: string | null;
  sourceAppId?: string | null;
  sourceLocationId?: string | null;
  sourceCtaId?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  source: string;
  locale?: string;
  abandonExistingDeals?: boolean;
  fuelType?: string | null;
};

export type ConsumerFinancing = {
  id: string;
  exists?: boolean;
  currentStepId: number | null;
  currentDetailsStepId: number | null;
  vehicleOwnershipStatusId: number | null;
  approximateBuyingTimeframeId: number | null;
  dealTypeId: number;
  dealStatusId: number | null;
  dealSource: string;
  createdAt: string;
  isPurchaseCompleted: boolean;
  vehicle: Vehicle | null;
  customers: ConsumerFinancingCustomer[];
  documentSummary: ConsumerFinancingDocuments;
  todos: ConsumerFinancingTodo[];
  receiptUrl: string | null;
  overwriteCandidate: {
    id: string;
    currentStepId: number | null;
    vehicle?: Vehicle;
  };
  lenderSubmission: LenderSubmission | null;
  sourceInfo: DealSourceInfo | null;
  pricing: ConsumerFinancingPricing;
  products: {
    id: string;
    name: string;
    discount: number;
    config: ConsumerFinancingProductConfiguration;
    addOns: ProductAddOn[];
  }[];
  dropOffDetails: ConsumerFinancingDropOffDetails | null;
};

export type ConsumerFinancingNotFoundResponse = {
  success: boolean;
  exists: boolean;
};

export type ConsumerFinancingPreview = {
  product: {
    config: ConsumerFinancingProductConfiguration;
  };
  pricing: ConsumerFinancingPricing;
};

export function tryGetActiveConsumerFinancingPurchase(
  consumerFinancing: ConsumerFinancing | PurchaseNotFoundResponse | undefined
): ConsumerFinancing | undefined {
  const activePurchase = consumerFinancing as ConsumerFinancing;

  return activePurchase && activePurchase.id ? activePurchase : undefined;
}
